import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types';
import './UserSidebar.css';

interface UserSidebarProps {
  users: User[];
  unassignedGroups?: { title: string; users: User[] }[];
  loading?: boolean;
  // Для сортировки "Руководителей практик" под конкретного пользователя.
  currentUserId?: number | null;
  onUserSelect?: () => void;
}

const UserSidebar: React.FC<UserSidebarProps> = ({
  users,
  unassignedGroups = [],
  loading = false,
  currentUserId = null,
  onUserSelect,
}) => {
  const navigate = useNavigate();
  const [expandedPracticeHeads, setExpandedPracticeHeads] = useState<Set<number>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollThumbHeight, setScrollThumbHeight] = useState(0);
  const [trackHeight, setTrackHeight] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  // Отладка: логируем полученные данные
  useEffect(() => {
    console.log('UserSidebar received users:', users);
    console.log('Users count:', users.length);
  }, [users]);

  const managingPartners = users.filter(user => user.role === 'managing_partner');
  const partners = users.filter(user => user.role === 'partner');
  const practiceHeads = users.filter((user) => user.role === 'practice_head');

  // Руководитель практики, который "привязан" к текущему пользователю (есть user в team_members) — должен быть сверху.
  // Важно: приводим к числу и проверяем, что team_members реально является массивом.
  const sortedPracticeHeads = useMemo(() => {
    if (!currentUserId) return practiceHeads;
    const currentIdNum = Number(currentUserId);
    const linkedHead = practiceHeads.find((head) => {
      if (!Array.isArray(head.team_members)) return false;
      return head.team_members.some((member) => Number(member.id) === currentIdNum);
    });

    if (!linkedHead) return practiceHeads;
    return [linkedHead, ...practiceHeads.filter((h) => h.id !== linkedHead.id)];
  }, [practiceHeads, currentUserId]);
  
  console.log('Filtered users - Managing Partners:', managingPartners.length, 'Partners:', partners.length, 'Practice Heads:', practiceHeads.length);

  const togglePracticeHead = (userId: number) => {
    const newExpanded = new Set(expandedPracticeHeads);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedPracticeHeads(newExpanded);
  };

  const handleUserClick = (user: User) => {
    navigate('/dashboard', {
      state: { selectedUserId: user.id },
      replace: false,
    });
    onUserSelect?.();
  };

  const handleLeaderClick = (user: User) => {
    // Для руководителей (руководители практик / партнёры / управляющие партнёры):
    // открываем профиль и раскрываем/сворачиваем его подчинённых.
    handleUserClick(user);
    togglePracticeHead(user.id);
  };

  // Вычисление размеров скроллбара
  useEffect(() => {
    const updateScrollbar = () => {
      const content = contentRef.current;
      if (!content) return;

      const scrollHeight = content.scrollHeight;
      const clientHeight = content.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      if (maxScroll <= 0) {
        setScrollThumbHeight(0);
        setScrollPosition(0);
        return;
      }

      // Высота ползунка пропорциональна видимой области
      const thumbHeight = (clientHeight / scrollHeight) * clientHeight;
      const finalThumbHeight = Math.max(thumbHeight, 20); // Минимум 20px
      setScrollThumbHeight(finalThumbHeight);

      // Позиция ползунка - вычисляем относительно доступной высоты для перемещения
      // Скроллбар находится на уровне .user-sidebar, но его высота соответствует clientHeight контента
      const currentTrackHeight = clientHeight; // Высота дорожки равна высоте видимой области контента
      setTrackHeight(currentTrackHeight);
      const scrollableTrackHeight = currentTrackHeight - finalThumbHeight;
      const scrollRatio = content.scrollTop / maxScroll;
      const thumbTop = scrollRatio * scrollableTrackHeight;
      setScrollPosition(Math.max(0, Math.min(scrollableTrackHeight, thumbTop)));
    };

    updateScrollbar();
    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', updateScrollbar);
      window.addEventListener('resize', updateScrollbar);
      // Также обновляем при изменении размера контента
      const resizeObserver = new ResizeObserver(updateScrollbar);
      resizeObserver.observe(content);
      
      return () => {
        content.removeEventListener('scroll', updateScrollbar);
        window.removeEventListener('resize', updateScrollbar);
        resizeObserver.disconnect();
      };
    }
  }, [expandedPracticeHeads, users]);

  // Обработка перетаскивания ползунка
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    if (contentRef.current) {
      dragStartScrollTop.current = contentRef.current.scrollTop;
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !contentRef.current || !thumbRef.current) return;

      const content = contentRef.current;
      const scrollHeight = content.scrollHeight;
      const clientHeight = content.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      if (maxScroll <= 0) return;

      const deltaY = e.clientY - dragStartY.current;
      const scrollbarHeight = clientHeight;
      const thumbHeight = scrollThumbHeight;
      const scrollableHeight = scrollbarHeight - thumbHeight;

      const scrollDelta = (deltaY / scrollableHeight) * maxScroll;
      const newScrollTop = Math.max(0, Math.min(maxScroll, dragStartScrollTop.current + scrollDelta));
      
      content.scrollTop = newScrollTop;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, scrollThumbHeight]);

  // Обработка клика на дорожку скроллбара
  const handleTrackClick = (e: React.MouseEvent) => {
    if (!contentRef.current || !scrollbarRef.current) return;
    
    const track = scrollbarRef.current;
    const trackRect = track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = trackRect.height;
    const thumbHeight = scrollThumbHeight;
    
    const content = contentRef.current;
    const scrollHeight = content.scrollHeight;
    const clientHeight = content.clientHeight;
    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll <= 0) return;

    const scrollableHeight = trackHeight - thumbHeight;
    const scrollRatio = clickY / scrollableHeight;
    const newScrollTop = scrollRatio * maxScroll;

    content.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
  };

  const renderUserItem = (user: User, isSubItem: boolean = false) => {
    const isExpanded = expandedPracticeHeads.has(user.id);
    const hasTeamMembers = user.team_members && user.team_members.length > 0;
    const isExpandableLeader =
      hasTeamMembers && (user.role === 'practice_head' || user.role === 'partner' || user.role === 'managing_partner');

    return (
      <div key={user.id} className={`user-item ${isSubItem ? 'sub-item' : ''}`}>
        <div
          className="user-item-content"
          onClick={(e) => {
            // Для подчиненных пользователей останавливаем всплытие события
            if (isSubItem) {
              e.stopPropagation();
            }
            if (isExpandableLeader) {
              handleLeaderClick(user);
            } else {
              handleUserClick(user);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="user-avatar-wrapper">
            <img
              src={user.avatar || '/avatar.png'}
              alt={user.name}
              className="user-avatar"
            />
            {user.is_online && <span className="online-indicator"></span>}
          </div>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            {user.practice && !isSubItem && (
              <div className="user-practice">{user.practice.name}</div>
            )}
          </div>
          {isExpandableLeader && (
            <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L1 4H11L6 9Z" fill="#999"/>
              </svg>
            </div>
          )}
        </div>
        {isExpandableLeader && (
          <div className={`team-members ${isExpanded ? 'expanded' : ''}`}>
            {user.team_members!.map(member => renderUserItem(member, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-sidebar">
      <div 
        className="user-sidebar-content"
        ref={contentRef}
      >
        {loading ? (
          <div className="user-sidebar-loading">
            <div className="user-sidebar-loading-spinner">
              <div className="user-sidebar-loading-spinner-ring"></div>
              <div className="user-sidebar-loading-spinner-ring"></div>
              <div className="user-sidebar-loading-spinner-ring"></div>
            </div>
            <p className="user-sidebar-loading-text">Загрузка сотрудников...</p>
          </div>
        ) : (
          <>
            {managingPartners.length > 0 && (
          <div className="user-section">
            <div className="section-title">Управляющие партнеры</div>
            <div className="user-list">
              {managingPartners.map(user => renderUserItem(user))}
            </div>
          </div>
        )}

        {partners.length > 0 && (
          <div className="user-section">
            <div className="section-title">Партнеры</div>
            <div className="user-list">
              {partners.map(user => renderUserItem(user))}
            </div>
          </div>
        )}

        {practiceHeads.length > 0 && (
          <div className="user-section">
            <div className="section-title">Руководители практик</div>
            <div className="user-list">
              {sortedPracticeHeads.map(user => renderUserItem(user))}
            </div>
          </div>
        )}

        {unassignedGroups.length > 0 && (
          <>
            {unassignedGroups.map((group) => (
              <div key={group.title} className="user-section">
                <div className="section-title">{group.title}</div>
                <div className="user-list">
                  {group.users.map((u) => renderUserItem(u))}
                </div>
              </div>
            ))}
          </>
        )}
          </>
        )}
      </div>
      {scrollThumbHeight > 0 && trackHeight > 0 && (
        <div 
          className="custom-scrollbar-track user-sidebar-scrollbar-track"
          ref={scrollbarRef}
          onClick={handleTrackClick}
          style={{
            height: `${trackHeight}px`,
          }}
        >
          <div
            className="custom-scrollbar-thumb"
            ref={thumbRef}
            style={{
              height: `${scrollThumbHeight}px`,
              top: `${scrollPosition}px`,
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </div>
      )}
    </div>
  );
};

export default UserSidebar;

